import { useCallback, useMemo } from 'react';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { fetchAccountOnboardingActions } from 'api/account';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';

type ChecklistItem =
    | 'verifyEmail'
    | 'createCampaign'
    | 'connectPayoutAccount'
    | 'raiseYourFirstDollar';
type Checklist = Record<ChecklistItem, boolean>;

const useChecklistData = () => {
    const { accountId } = useCurrentAccount();
    const { data, isRefetching } = useSuspenseQuery({
        queryKey: accountQueryKeys.requiredChecklist(accountId),
        queryFn: () => fetchAccountOnboardingActions(accountId),
        select: data => {
            // missing data from backfill
            if (data.length === 0) {
                return {
                    checklist: {
                        verifyEmail: false,
                        createCampaign: false,
                        connectPayoutAccount: false,
                        raiseYourFirstDollar: false,
                    },
                    data: [],
                    completedAllActions: false,
                };
            }

            const checklist = data.reduce((prev, curr) => {
                switch (curr.action_item) {
                    case 'verify_email':
                        prev.verifyEmail = !!curr.completed_at;
                        break;
                    case 'connect_payout_account':
                        prev.connectPayoutAccount = !!curr.completed_at;
                        break;
                    case 'create_first_campaign':
                        prev.createCampaign = !!curr.completed_at;
                        break;
                    case 'share_campaign':
                        prev.raiseYourFirstDollar = !!curr.completed_at;
                        break;
                    default:
                        break;
                }
                return prev;
            }, {} as Checklist);
            const requiredData = data.filter(data => data.is_required);
            const completedAllActions =
                Object.values(checklist).filter(value => value === false).length === 0;
            return {
                checklist,
                data: requiredData,
                completedAllActions,
            };
        },
        refetchOnWindowFocus: 'always',
    });

    return { ...data, isRefetching };
};

const useInvalidateChecklistQuery = () => {
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();

    const invalidateChecklistQuery = useCallback(() => {
        queryClient.invalidateQueries({
            queryKey: accountQueryKeys.requiredChecklist(accountId),
        });
    }, [queryClient, accountId]);

    return {
        invalidateChecklistQuery,
    };
};

const useCompletedAtLeastOneChecklistItem = () => {
    const { checklist } = useChecklistData();
    const completedAtLeastOne = useMemo(() => {
        const numCompleted = Object.values(checklist).filter(item => item).length;
        return numCompleted >= 1;
    }, [checklist]);

    return completedAtLeastOne;
};

export { useChecklistData, useInvalidateChecklistQuery, useCompletedAtLeastOneChecklistItem };
